import { useTheme, Box, Popper, Typography, Button } from '@mui/material';
import { createElement, useState } from 'react';
import { DBRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import TypeText from '../form/form-fields/TypeText';
import DBMultiSearch from '../special/DBMultiSearch';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import ExoProcessing from './ExoProcessing';
import AddIcon from '@mui/icons-material/Add';
import * as yup from 'yup';

export const ExoAutoCompleteSelector = ({
  onSelectionChange,
  selection,
  multiselect,
  label,
  searchParams,
  size,
  onAdd,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const [text, setText] = useState('');
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'search-popper' : undefined;

  function handleUpdateTextfield(key, string) {
    setText(string);
    setIsInvalidEmail(checkTextForValidEmail(string));
  }

  function handleSelect(element) {
    DBRequest({
      config,
      path: `${element.path}/${element.id}`,
      onResponse: (res) => handleResponse(res, element.type),
      onLoading: setIsLoading,
    });
  }

  function handleResponse(res, type) {
    const typeMap = {
      Contacts: 'contact',
      Companies: 'company',
      'Company Leads': 'companyLead',
      'Person Leads': 'personLead',
      Employees: 'user',
    };

    if (multiselect) {
      const oldData = selection || [];
      onSelectionChange([...oldData, res.data], typeMap[type]);
    } else {
      onSelectionChange(res.data, typeMap[type]);
    }
  }

  function checkTextForValidEmail(email) {
    const schema = yup.string().email().required();
    try {
      schema.validateSync(email);
      return true;
    } catch (err) {
      return false;
    }
  }

  return (
    <>
      <Box className="w-full flex items-center gap-2">
        <TypeText
          field={{
            label: label || t('Search'),
            key: 'search',
            // recipients are mostly typed as addresses
            inputMode: onAdd ? 'email' : undefined,
          }}
          dataCollection={{ search: text }}
          updateValidationOnChange={handleUpdateTextfield}
          onFocus={handleClick}
          onBlur={() => setAnchorEl(null)}
          size={size}
        />
        {isInvalidEmail && onAdd && (
          <Button
            onClick={() => {
              onAdd(text);
              setText('');
              setIsInvalidEmail(false);
            }}
            aria-label={t('Add')}
            sx={{ bgcolor: colors.bgInput, minWidth: '44px', minHeight: '40px' }}
            className="h-full"
          >
            <AddIcon />
          </Button>
        )}
      </Box>

      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        sx={{
          bgcolor: colors.glass,
          zIndex: 9999,

          opacity: searchResults?.length ? 1 : 0,
        }}
        placement="bottom-start"
        className=" w-max-full overflow-hidden"
      >
        <Box
          sx={{ bgcolor: colors.glassSolid }}
          className="flex flex-col h-[200px] overflow-x-hidden pr-1 rounded-md"
        >
          <DBMultiSearch
            params={searchParams}
            onSearch={setSearchResults}
            value={text}
            hideSearchBar
          >
            {searchResults?.map((element, index) => (
              <Box
                key={'searchResult' + index}
                sx={{
                  border: `1px solid transparent`,
                  ':hover': {
                    border: `1px solid ${colors.info}`,
                    bgcolor: colors.info + '30',
                  },
                }}
                // min(…): a fixed 400px was wider than a phone screen
                className=" rounded-md transition-all cursor-pointer min-w-[min(400px,90vw)] max-w-[90vw] w-full px-2 py-1 flex gap-3 items-center relative min-h-[44px]"
                onMouseDown={() => handleSelect(element)}
              >
                <Box sx={{ bgcolor: colors.card }} className="rounded-md p-2">
                  {element.Icon ? (
                    createElement(element.Icon)
                  ) : (
                    <QuestionMarkIcon />
                  )}
                </Box>
                <Box className="flex flex-col">
                  <Typography
                    className=" opacity-50 "
                    fontWeight={600}
                    fontSize={10}
                  >
                    {element.subLabel}
                  </Typography>
                  <Typography fontWeight={600}>{element.label}</Typography>
                  <Typography className=" opacity-50 " fontSize={12}>
                    {element.address.street &&
                      `${element.address.street} ${element.address.houseNumber} - ${element.address.zipCode} ${element.address.city}`}
                  </Typography>
                </Box>
              </Box>
            ))}
          </DBMultiSearch>
        </Box>
      </Popper>
      <ExoProcessing open={isLoading} label={t('Loading Data')} />
    </>
  );
};
